import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
} from '@mui/material';
import { Business } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { api } from '@viilutalous/common';
import { useTenant } from '../contexts/TenantContext';

const COUNTRIES = [
  { code: 'FI', name: 'Finland' },
  { code: 'SE', name: 'Sweden' },
  { code: 'NO', name: 'Norway' },
  { code: 'DK', name: 'Denmark' },
  { code: 'EE', name: 'Estonia' },
  { code: 'DE', name: 'Germany' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'US', name: 'United States' },
];

const CURRENCIES = [
  { code: 'EUR', name: 'Euro (EUR)' },
  { code: 'SEK', name: 'Swedish Krona (SEK)' },
  { code: 'NOK', name: 'Norwegian Krone (NOK)' },
  { code: 'DKK', name: 'Danish Krone (DKK)' },
  { code: 'GBP', name: 'British Pound (GBP)' },
  { code: 'USD', name: 'US Dollar (USD)' },
];

export default function CompanyInfoSetup({ onSuccess }) {
  const { t } = useTranslation();
  const { currentTenant } = useTenant();

  const [formData, setFormData] = useState({
    company_name: '',
    business_id: '',
    email: '',
    street_address: '',
    zip_code: '',
    city: '',
    country: 'FI',
    currency: 'EUR',
    iban_1: '',
    bic_1: '',
    bank_name_1: '',
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Load existing company info on mount
  useEffect(() => {
    const fetchCompanyInfo = async () => {
      if (!currentTenant?.id) return;

      try {
        const response = await api.get(`/${currentTenant.id}/company_info`);
        if (response.data.company_info && response.data.company_info.length > 0) {
          const info = response.data.company_info[0];
          setFormData({
            company_name: info.company_name || '',
            business_id: info.business_id || '',
            email: info.email || '',
            street_address: info.street_address || '',
            zip_code: info.zip_code || '',
            city: info.city || '',
            country: info.country || 'FI',
            currency: info.currency || 'EUR',
            iban_1: info.iban_1 || '',
            bic_1: info.bic_1 || '',
            bank_name_1: info.bank_name_1 || '',
          });
        }
      } catch (err) {
        console.error('Error fetching company info:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchCompanyInfo();
  }, [currentTenant?.id]);

  const handleChange = (field) => (e) => {
    setFormData((prev) => ({
      ...prev,
      [field]: e.target.value,
    }));
    setError('');
  };

  const validateForm = () => {
    const requiredFields = [
      { field: 'company_name', label: t('setup.company_name') },
      { field: 'business_id', label: t('registration.business_id') },
      { field: 'email', label: t('setup.email') },
      { field: 'street_address', label: t('setup.street_address') },
      { field: 'zip_code', label: t('setup.zip_code') },
      { field: 'city', label: t('setup.city') },
      { field: 'iban_1', label: t('setup.iban') },
      { field: 'bic_1', label: t('setup.bic') },
      { field: 'bank_name_1', label: t('setup.bank_name') },
    ];

    for (const { field, label } of requiredFields) {
      if (!formData[field]?.trim()) {
        setError(`${label}: ${t('setup.required')}`);
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setSaving(true);
    setError('');

    try {
      await api.post(`/${currentTenant.id}/company_info`, formData);
      setSuccess(t('setup.success'));

      // Wait a moment then refresh tenants (which re-checks setup status)
      setTimeout(() => {
        onSuccess?.();
      }, 1000);
    } catch (err) {
      const errorMessage = err.response?.data?.detail || t('setup.error');
      setError(typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 4,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
      }}
    >
      <Card sx={{ maxWidth: 600, width: '100%' }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Business sx={{ fontSize: 40, mr: 2, color: 'primary.main' }} />
            <Box>
              <Typography variant="h5">{t('setup.title')}</Typography>
              <Typography variant="body2" color="text.secondary">
                {t('setup.description')}
              </Typography>
            </Box>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          {/* Company Information Section */}
          <Typography variant="subtitle1" sx={{ mt: 3, mb: 2, fontWeight: 'bold' }}>
            {t('setup.company_info')}
          </Typography>

          <Stack spacing={2}>
            <TextField
              fullWidth
              label={t('registration.company_name')}
              value={formData.company_name}
              onChange={handleChange('company_name')}
              disabled={saving || !!success}
              required
            />

            <TextField
              fullWidth
              label={t('registration.business_id')}
              value={formData.business_id}
              onChange={handleChange('business_id')}
              placeholder="1234567-8"
              disabled={saving || !!success}
              required
            />

            <TextField
              fullWidth
              label={t('setup.email')}
              value={formData.email}
              onChange={handleChange('email')}
              type="email"
              disabled={saving || !!success}
              required
            />

            <FormControl fullWidth disabled={saving || !!success}>
              <InputLabel>{t('setup.currency')}</InputLabel>
              <Select
                value={formData.currency}
                label={t('setup.currency')}
                onChange={handleChange('currency')}
              >
                {CURRENCIES.map((c) => (
                  <MenuItem key={c.code} value={c.code}>
                    {c.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          <Divider sx={{ my: 3 }} />

          {/* Address Section */}
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
            {t('setup.address')}
          </Typography>

          <Stack spacing={2}>
            <TextField
              fullWidth
              label={t('setup.street_address')}
              value={formData.street_address}
              onChange={handleChange('street_address')}
              disabled={saving || !!success}
              required
            />

            <Stack direction="row" spacing={2}>
              <TextField
                label={t('setup.zip_code')}
                value={formData.zip_code}
                onChange={handleChange('zip_code')}
                disabled={saving || !!success}
                required
                sx={{ width: '35%' }}
              />
              <TextField
                label={t('setup.city')}
                value={formData.city}
                onChange={handleChange('city')}
                disabled={saving || !!success}
                required
                sx={{ flexGrow: 1 }}
              />
            </Stack>

            <FormControl fullWidth disabled={saving || !!success}>
              <InputLabel>{t('setup.country')}</InputLabel>
              <Select
                value={formData.country}
                label={t('setup.country')}
                onChange={handleChange('country')}
              >
                {COUNTRIES.map((c) => (
                  <MenuItem key={c.code} value={c.code}>
                    {c.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          <Divider sx={{ my: 3 }} />

          {/* Bank Account Section */}
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
            {t('setup.bank_info')}
          </Typography>

          <Stack spacing={2}>
            <TextField
              fullWidth
              label={t('setup.bank_name')}
              value={formData.bank_name_1}
              onChange={handleChange('bank_name_1')}
              disabled={saving || !!success}
              required
            />

            <TextField
              fullWidth
              label={t('setup.iban')}
              value={formData.iban_1}
              onChange={handleChange('iban_1')}
              placeholder="FI00 0000 0000 0000 00"
              disabled={saving || !!success}
              required
            />

            <TextField
              fullWidth
              label={t('setup.bic')}
              value={formData.bic_1}
              onChange={handleChange('bic_1')}
              placeholder="NDEAFIHH"
              disabled={saving || !!success}
              required
            />
          </Stack>

          <Box sx={{ mt: 4 }}>
            <Button
              variant="contained"
              size="large"
              onClick={handleSubmit}
              disabled={saving || !!success}
              fullWidth
            >
              {saving ? <CircularProgress size={24} /> : t('setup.submit')}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
